/**
 * Central SEO constants and helpers for the web build.
 *
 * Per-route metadata is rendered through `expo-router/head` (`<Head>`, wrapped
 * by the `<Seo>` component). Truly global tags — charset, viewport, icons,
 * manifest, theme-color — live in `app/+html.tsx`. This module is the single
 * source of truth for the values both paths share so a copy change lands in
 * one place.
 */

/** Canonical origin for the web app. Overridable per environment
 *  (staging serves from `staging.kiips.app`); defaults to production. */
export const SITE_URL = (
  process.env.EXPO_PUBLIC_SITE_URL ?? 'https://kiips.app'
).replace(/\/$/, '');

export const SITE_NAME = 'Kiips';

/** Default `<title>` and the suffix appended to per-page titles, e.g.
 *  `Pricing · Kiips`. Pages pass the bare page name to `<Seo>`. */
export const DEFAULT_TITLE = 'Kiips — Plan trips with places, days and timelines';
export const TITLE_SUFFIX = `· ${SITE_NAME}`;

export const DEFAULT_DESCRIPTION =
  'Plan trips with places, days and timelines. Kiips turns scattered bookings and ideas into a clear day-by-day itinerary.';

/** Brand colour for `+html.tsx` theme-color — an indigo between the two Aurora
 *  gradient stops so the browser chrome harmonises with the gradient icon. The
 *  build side keeps its own copy (`THEME_COLOR` in `scripts/lib/brand.mjs`,
 *  used for the manifest) since app code can't import a build script across the
 *  module boundary — keep the two values equal. */
export const THEME_COLOR = '#4F46E5';

/** Brand ink — the app's near-black surface, used for the web splash overlay
 *  and the document background in `+html.tsx`. Mirrors `INK` in
 *  `scripts/lib/brand.mjs` (app code can't import a build script across the
 *  module boundary) and the native splash `backgroundColor` in `app.json` —
 *  keep all three equal. */
export const BRAND_INK = '#0B0B0C';

/** Default Open Graph / Twitter share image (1200×630). Generated into
 *  `apps/mobile/public/` by `scripts/generate-web-icons.mjs`. */
export const DEFAULT_OG_IMAGE = '/og-image.png';

/** Dimensions / type of every share image we emit — the static brand card
 *  (`og-image.png`) and the dynamic per-link cards (`api/og/[linkId].ts`) are
 *  all 1200×630 PNGs. Declaring these inline lets Facebook-family crawlers
 *  (WhatsApp, Messenger, Facebook) lay out the preview without first
 *  downloading the image, which is what makes the thumbnail appear on the
 *  FIRST share instead of only after the async image fetch warms the cache. */
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const OG_IMAGE_TYPE = 'image/png';
export const OG_IMAGE_ALT = 'Kiips — Painless planning. Treasured trips.';

/** Compose a full page title. A bare page name gets the brand suffix; the
 *  home page (no name) uses the default title verbatim. */
export function pageTitle(name?: string): string {
  if (!name) return DEFAULT_TITLE;
  return `${name} ${TITLE_SUFFIX}`;
}

/** Resolve a path (or already-absolute URL) to an absolute URL on SITE_URL. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Per-route metadata overrides, keyed by canonical pathname. */
export interface RouteSeoEntry {
  title?: string;
  description?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
}

/**
 * Single source of truth for per-route page metadata. The root layout looks up
 * the active pathname here and renders one `<Seo>`, so individual screens no
 * longer hand-carry (and forget to update) their own `<Seo>` block. A path
 * present here is treated as public/indexable and gets a canonical URL; any
 * path NOT listed falls back to the default title/description with no canonical
 * — matching how dynamic/app routes behaved before. Keep the public entries in
 * sync with `PUBLIC_ROUTES` in `scripts/generate-seo-files.mjs`.
 */
export const SEO_BY_PATH: Record<string, RouteSeoEntry> = {
  '/': {},
  '/pricing': {
    title: 'Pricing',
    description:
      'Simple plans for every kind of trip. Start free and upgrade when your itinerary grows.',
  },
  '/login': {
    title: 'Sign In',
    description: 'Sign in to keep your trips in sync across devices.',
  },
  '/signup': {
    title: 'Sign Up',
    description: 'Create a free Kiips account and start planning your next trip.',
  },
  '/family': {
    title: 'Family Travel Planning',
    description:
      'Plan trips together — shared itineraries, days, and places for the whole family.',
  },
  '/integrations': {
    title: 'Integrations',
    description: 'Connect Kiips with the calendars and tools you already use.',
  },
  '/contact-us': {
    title: 'Contact Us',
    description: 'Questions, feedback, or support — get in touch with the Kiips team.',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'How Kiips collects, uses, and protects your data.',
  },
  '/terms': {
    title: 'Terms of Service',
    description: 'The terms that govern your use of Kiips.',
  },
  // Utility screen: titled but kept out of the index.
  '/forgot-password': { title: 'Reset Password', noindex: true },
};
